/**
 * VenueFlow Commercial Module — Airtable Service
 * 
 * API functions for Inventory, Deals, Booking Lines, Entitlements, Playlists
 */

const COMMERCIAL_BASE_ID = process.env.REACT_APP_COMMERCIAL_BASE_ID;
const COMMERCIAL_PAT = process.env.REACT_APP_COMMERCIAL_PAT;

// Table IDs - update after creating tables
const TABLES = {
  inventory: process.env.REACT_APP_INVENTORY_TABLE_ID,
  deals: process.env.REACT_APP_DEALS_TABLE_ID,
  bookingLines: process.env.REACT_APP_BOOKING_LINES_TABLE_ID,
  entitlements: process.env.REACT_APP_ENTITLEMENTS_TABLE_ID,
  playlists: process.env.REACT_APP_PLAYLISTS_TABLE_ID,
  playlistSlots: process.env.REACT_APP_PLAYLIST_SLOTS_TABLE_ID,
  partners: process.env.REACT_APP_PARTNERS_TABLE_ID,
  venues: process.env.REACT_APP_VENUES_TABLE_ID,
  fixtures: process.env.REACT_APP_COMMERCIAL_FIXTURES_TABLE_ID || process.env.REACT_APP_FIXTURES_TABLE_ID,
  assets: process.env.REACT_APP_ASSETS_TABLE_ID,
  tvcReels: process.env.REACT_APP_TVC_REELS_TABLE_ID,
  tvcReelSlots: process.env.REACT_APP_TVC_REEL_SLOTS_TABLE_ID,
};

// ============================================================
// BASE FETCH HELPER
// ============================================================
async function airtableFetch(endpoint, options = {}, _retries = 0) {
  if (!COMMERCIAL_BASE_ID) {
    throw new Error("Commercial base not configured — add REACT_APP_COMMERCIAL_BASE_ID to environment variables");
  }
  const url = `https://api.airtable.com/v0/${COMMERCIAL_BASE_ID}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${COMMERCIAL_PAT}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Retry on rate limit (429) with exponential backoff — up to 3 retries
  if (response.status === 429 && _retries < 3) {
    const delay = Math.pow(2, _retries) * 1000 + Math.random() * 500; // 1s, 2s, 4s + jitter
    console.warn(`[commercialAirtable] Rate limited — retry ${_retries + 1}/3 in ${Math.round(delay)}ms`);
    await new Promise(r => setTimeout(r, delay));
    return airtableFetch(endpoint, options, _retries + 1);
  }

  if (!response.ok) {
    const error = await response.text();
    console.error('[commercialAirtable] Error:', error);
    throw new Error(`Airtable error: ${response.status} — ${error}`);
  }

  return response.json();
}

// ============================================================
// GENERIC CRUD HELPERS
// ============================================================
async function fetchAll(tableId, options = {}) {
  const { filterByFormula, sort, view, maxRecords, cellFormat, timeZone, userLocale } = options;
  const params = new URLSearchParams();
  
  if (filterByFormula) params.set('filterByFormula', filterByFormula);
  if (view) params.set('view', view);
  if (maxRecords) params.set('maxRecords', maxRecords);
  if (cellFormat) params.set('cellFormat', cellFormat);
  if (timeZone) params.set('timeZone', timeZone);
  if (userLocale) params.set('userLocale', userLocale);
  if (sort) {
    sort.forEach((s, i) => {
      params.set(`sort[${i}][field]`, s.field);
      params.set(`sort[${i}][direction]`, s.direction || 'asc');
    });
  }

  const queryString = params.toString();
  const baseEndpoint = `/${tableId}${queryString ? `?${queryString}` : ''}`;
  
  let allRecords = [];
  let offset = null;

  do {
    const endpoint = offset
      ? `${baseEndpoint}${queryString ? '&' : '?'}offset=${offset}`
      : baseEndpoint;
    const data = await airtableFetch(endpoint);
    allRecords = [...allRecords, ...(data.records || [])];
    offset = data.offset;
  } while (offset);

  return allRecords;
}

async function fetchOne(tableId, recordId) {
  const data = await airtableFetch(`/${tableId}/${recordId}`);
  return data;
}

async function createRecord(tableId, fields) {
  const data = await airtableFetch(`/${tableId}`, {
    method: 'POST',
    body: JSON.stringify({ fields }),
  });
  return data;
}

async function createRecords(tableId, records) {
  // Airtable limit: 10 records per request
  const results = [];
  for (let i = 0; i < records.length; i += 10) {
    const chunk = records.slice(i, i + 10).map(fields => ({ fields }));
    const data = await airtableFetch(`/${tableId}`, {
      method: 'POST',
      body: JSON.stringify({ records: chunk }),
    });
    results.push(...(data.records || []));
  }
  return results;
}

async function updateRecord(tableId, recordId, fields) {
  const data = await airtableFetch(`/${tableId}/${recordId}`, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  });
  return data;
}

async function deleteRecord(tableId, recordId) {
  const data = await airtableFetch(`/${tableId}/${recordId}`, {
    method: 'DELETE',
  });
  return data;
}

// ============================================================
// INVENTORY
// ============================================================
export async function getInventory(options = {}) {
  return fetchAll(TABLES.inventory, options);
}

export async function getInventoryByCompetition(competition) {
  return fetchAll(TABLES.inventory, {
    filterByFormula: `{Competition}="${competition}"`,
  });
}

export async function getInventoryBySource(source) {
  return fetchAll(TABLES.inventory, {
    filterByFormula: `{Source}="${source}"`,
  });
}

export async function createInventory(data) {
  return createRecord(TABLES.inventory, data);
}

export async function updateInventory(recordId, data) {
  return updateRecord(TABLES.inventory, recordId, data);
}

export async function deleteInventory(recordId) {
  return deleteRecord(TABLES.inventory, recordId);
}

/**
 * Recalculate "Available Minutes" on an inventory record after booking lines change.
 * Sums minutes used by all linked booking lines, then updates:
 *   Available Minutes = Total Minutes - Used Minutes
 * @param {string} inventoryId - Airtable record ID of the inventory
 */
export async function recalculateInventoryAvailable(inventoryId) {
  if (!inventoryId) return;

  // 1. Fetch the inventory record
  const inv = await fetchOne(TABLES.inventory, inventoryId);
  if (!inv) return;
  const totalMinutes = inv.fields["Total Minutes"] || 0;

  // 2. Fetch all booking lines linked to this inventory
  const lines = await fetchAll(TABLES.bookingLines, {
    filterByFormula: `FIND("${inventoryId}", ARRAYJOIN({Inventory}))`,
  });

  // 3. Calculate used minutes
  const DURATION_MAP = { "10s": 1/6, "15s": 0.25, "30s": 0.5, "60s": 1, "90s": 1.5, "120s": 2 };
  const usedMinutes = lines.reduce((sum, line) => {
    const f = line.fields;
    // Non-inventory lines don't consume minutes
    if (f["Is Non-Inventory"]) return sum;
    const spots = f["Spots Per Match"] || 0;
    const duration = Array.isArray(f["Slot Duration"]) ? f["Slot Duration"][0] : (f["Slot Duration"] || "30s");
    const games = f["Number of Games"] || f["Games Count"] || 0;
    const durationMins = DURATION_MAP[duration] || 0.5;
    return sum + (spots * durationMins * games);
  }, 0);

  // 4. Log the calculated availability (field is computed in Airtable, so we don't write it)
  const available = Math.max(0, totalMinutes - usedMinutes);
  console.log(`[commercialAirtable] Inventory ${inventoryId}: ${totalMinutes} total - ${usedMinutes.toFixed(2)} used = ${available.toFixed(2)} available`);
}

/**
 * Get available inventory for a venue/competition
 * Used for validation when creating booking lines
 */
export async function getAvailableInventory(source, competition) {
  const records = await fetchAll(TABLES.inventory, {
    filterByFormula: `AND({Source}="${source}", {Competition}="${competition}")`,
  });
  
  if (records.length === 0) return null;
  
  const inventory = records[0];
  return {
    id: inventory.id,
    source: inventory.fields['Source'],
    totalMinutes: inventory.fields['Total Minutes'] || 0,
    returnedMinutes: inventory.fields['Returned Minutes'] || 0,
    availableMinutes: inventory.fields['Available Minutes'] || 0,
  };
}

// ============================================================
// DEALS
// ============================================================
export async function getDeals(options = {}) {
  return fetchAll(TABLES.deals, {
    sort: [{ field: 'Deal ID', direction: 'desc' }],
    ...options,
  });
}

export async function getDealsByStatus(status) {
  return fetchAll(TABLES.deals, {
    filterByFormula: `{Status}="${status}"`,
    sort: [{ field: 'Deal ID', direction: 'desc' }],
  });
}

export async function getDealsByBM(businessManager) {
  return fetchAll(TABLES.deals, {
    filterByFormula: `{Business Manager}="${businessManager}"`,
    sort: [{ field: 'Deal ID', direction: 'desc' }],
  });
}

export async function getDealsByClient(clientId) {
  return fetchAll(TABLES.deals, {
    filterByFormula: `FIND("${clientId}", ARRAYJOIN({Client}))`,
    sort: [{ field: 'Deal ID', direction: 'desc' }],
  });
}

export async function getDeal(recordId) {
  return fetchOne(TABLES.deals, recordId);
}

export async function createDeal(data) {
  return createRecord(TABLES.deals, data);
}

export async function updateDeal(recordId, data) {
  return updateRecord(TABLES.deals, recordId, data);
}

export async function deleteDeal(recordId) {
  return deleteRecord(TABLES.deals, recordId);
}

// ============================================================
// BOOKING LINES
// ============================================================
export async function getBookingLines(options = {}) {
  return fetchAll(TABLES.bookingLines, options);
}

export async function getBookingLinesByDeal(dealId) {
  // Fetch the deal record to get linked booking line IDs directly,
  // avoiding the need to know the reverse-link field name on the booking lines table.
  const deal = await fetchOne(TABLES.deals, dealId);
  const lineIds = deal?.fields?.["Booking Lines"] || [];
  if (lineIds.length === 0) return [];

  // Fetch each booking line by record ID (batch-friendly via OR formula)
  const chunks = [];
  for (let i = 0; i < lineIds.length; i += 15) {
    const batch = lineIds.slice(i, i + 15);
    const formula = `OR(${batch.map(id => `RECORD_ID()="${id}"`).join(",")})`;
    const records = await fetchAll(TABLES.bookingLines, { filterByFormula: formula });
    chunks.push(...records);
  }
  return chunks;
}

export async function getBookingLine(recordId) {
  return fetchOne(TABLES.bookingLines, recordId);
}

export async function createBookingLine(data) {
  // Strip "Deals" field — the reverse link field name is unknown.
  // Callers should use linkBookingLineToDeal() after creation instead.
  const { Deals, ...cleanData } = data;
  return createRecord(TABLES.bookingLines, cleanData);
}

/**
 * Link a booking line to a deal by updating the deal's "Booking Lines" linked field.
 * This avoids needing to know the reverse-link field name on the booking lines table.
 * @param {string} dealId - Airtable record ID of the deal
 * @param {string} lineId - Airtable record ID of the newly created booking line
 */
export async function linkBookingLineToDeal(dealId, lineId) {
  const deal = await fetchOne(TABLES.deals, dealId);
  const existing = deal?.fields?.["Booking Lines"] || [];
  await updateRecord(TABLES.deals, dealId, {
    "Booking Lines": [...existing, lineId],
  });
}

export async function updateBookingLine(recordId, data) {
  return updateRecord(TABLES.bookingLines, recordId, data);
}

export async function deleteBookingLine(recordId) {
  return deleteRecord(TABLES.bookingLines, recordId);
}

/**
 * Get sold minutes for a venue/competition
 * Sum of all booking lines for that venue
 */
export async function getSoldMinutes(source, competition) {
  const lines = await fetchAll(TABLES.bookingLines, {
    filterByFormula: `AND(FIND("${source}", ARRAYJOIN({Venue})), {Competition}="${competition}")`,
  });
  
  return lines.reduce((total, line) => {
    const spotsPerMatch = line.fields['Spots Per Match'] || 0;
    const duration = line.fields['Slot Duration'] || '30s';
    const gamesCount = line.fields['Games Count'] || 0;
    
    const durationMins = duration === '15s' ? 0.25 : duration === '60s' ? 1 : 0.5;
    return total + (spotsPerMatch * durationMins * gamesCount);
  }, 0);
}

/**
 * Check if inventory is available for a new booking line
 */
export async function checkInventoryAvailability(source, competition, requestedMinutes) {
  const inventory = await getAvailableInventory(source, competition);
  
  if (!inventory) {
    return {
      available: false,
      reason: `No inventory found for ${source} / ${competition}`,
      inventoryMinutes: 0,
      soldMinutes: 0,
      remainingMinutes: 0,
      requestedMinutes,
    };
  }
  
  const soldMinutes = await getSoldMinutes(source, competition);
  const remainingMinutes = inventory.availableMinutes - soldMinutes;
  
  return {
    available: requestedMinutes <= remainingMinutes,
    reason: requestedMinutes > remainingMinutes 
      ? `Only ${remainingMinutes.toFixed(1)} mins available, requested ${requestedMinutes.toFixed(1)} mins`
      : null,
    inventoryMinutes: inventory.availableMinutes,
    soldMinutes,
    remainingMinutes,
    requestedMinutes,
  };
}

// ============================================================
// ENTITLEMENTS
// ============================================================
export async function getEntitlements(options = {}) {
  return fetchAll(TABLES.entitlements, options);
}

export async function getEntitlementsByBookingLine(bookingLineId) {
  return fetchAll(TABLES.entitlements, {
    filterByFormula: `FIND("${bookingLineId}", ARRAYJOIN({Booking Lines}))`,
  });
}

export async function getEntitlementsByClient(clientId) {
  return fetchAll(TABLES.entitlements, {
    filterByFormula: `FIND("${clientId}", ARRAYJOIN({Client}))`,
    sort: [{ field: 'Fixture', direction: 'asc' }],
  });
}

export async function getEntitlementsByFixture(fixtureId) {
  return fetchAll(TABLES.entitlements, {
    filterByFormula: `FIND("${fixtureId}", ARRAYJOIN({Fixture}))`,
  });
}

export async function getEntitlement(recordId) {
  return fetchOne(TABLES.entitlements, recordId);
}

export async function createEntitlements(entitlements) {
  return createRecords(TABLES.entitlements, entitlements);
}

export async function updateEntitlement(recordId, data) {
  return updateRecord(TABLES.entitlements, recordId, data);
}

export async function deleteEntitlement(recordId) {
  return deleteRecord(TABLES.entitlements, recordId);
}

/**
 * Update content assignment for an entitlement
 */
export async function assignContent(entitlementId, contentIds, allocationMode = 'Individual') {
  return updateRecord(TABLES.entitlements, entitlementId, {
    'Content': contentIds,
    'Allocation Mode': allocationMode,
    'Allocation Status': contentIds.length > 0 ? 'Complete' : 'Pending',
  });
}

// ============================================================
// PLAYLISTS
// ============================================================
export async function getPlaylists(options = {}) {
  return fetchAll(TABLES.playlists, {
    sort: [{ field: 'Playlist ID', direction: 'desc' }],
    ...options,
  });
}

export async function getPlaylistByFixture(fixtureId) {
  const records = await fetchAll(TABLES.playlists, {
    filterByFormula: `FIND("${fixtureId}", ARRAYJOIN({Fixture}))`,
  });
  return records[0] || null;
}

/**
 * Scoped playlist lookup — finds existing playlist matching fixture ID, round, and venue.
 * Used by generatePlaylist to prevent duplicate playlist creation.
 * Falls back to fixtureId-only match if scoped search finds nothing.
 */
export async function getPlaylistByFixtureScoped(fixtureId, round, venue) {
  if (!TABLES.playlists || !fixtureId) return null;
  // First try: match by fixture record ID (most reliable)
  const byFixture = await fetchAll(TABLES.playlists, {
    filterByFormula: `FIND("${fixtureId}", ARRAYJOIN({Fixture}))`,
  });
  if (byFixture.length > 0) return byFixture[0];
  // Second try: match by round + venue name (cellFormat=string)
  if (round && venue) {
    const aliases = VENUE_ALIASES[venue] || [venue];
    const venueChecks = aliases.map(a => `FIND("${a}", ARRAYJOIN({Venue}))`).join(',');
    const formula = `AND({Round}="${round}", OR(${venueChecks}))`;
    try {
      let allRecords = [];
      let offset = null;
      do {
        let url = `/${TABLES.playlists}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
        url += `&filterByFormula=${encodeURIComponent(formula)}`;
        if (offset) url += `&offset=${offset}`;
        const data = await airtableFetch(url);
        allRecords = [...allRecords, ...(data.records || [])];
        offset = data.offset;
      } while (offset);
      if (allRecords.length > 0) return allRecords[0];
    } catch (e) {
      console.warn('[commercialAirtable] getPlaylistByFixtureScoped fallback failed:', e.message);
    }
  }
  return null;
}

/**
 * Get playlist slots for a specific playlist ID.
 * @param {string} playlistId - Airtable record ID of the playlist
 * @returns {Object[]} Array of slot records
 */
export async function getPlaylistSlots(playlistId) {
  if (!TABLES.playlistSlots || !playlistId) return [];

  // Strategy 1: Try "Playlist Record ID" formula field (text match — fast if it exists)
  try {
    const results = await fetchAll(TABLES.playlistSlots, {
      filterByFormula: `{Playlist Record ID}="${playlistId}"`,
    });
    if (results.length > 0) {
      console.log(`[getPlaylistSlots] Found ${results.length} slots via {Playlist Record ID}`);
      return results;
    }
  } catch (e) {
    // Field doesn't exist — fall through
  }

  // Strategy 2: Filter by linked Playlist field using ARRAYJOIN
  // Works reliably with Airtable linked record fields (returns arrays of record IDs)
  try {
    const results = await fetchAll(TABLES.playlistSlots, {
      filterByFormula: `FIND("${playlistId}", ARRAYJOIN({Playlist}))`,
    });
    if (results.length > 0) {
      console.log(`[getPlaylistSlots] Found ${results.length} slots via ARRAYJOIN({Playlist})`);
      return results;
    }
    // 0 results could mean empty playlist OR field name mismatch — fall through
  } catch (e) {
    console.warn(`[getPlaylistSlots] ARRAYJOIN strategy failed:`, e.message);
  }

  // Strategy 3: Read "Playlist Slots" backlink from the Playlist record itself
  try {
    const playlistRecord = await airtableFetch(`/${TABLES.playlists}/${playlistId}`);
    const slotIds = playlistRecord?.fields?.["Playlist Slots"] || [];
    if (slotIds.length > 0) {
      console.log(`[getPlaylistSlots] Fetching ${slotIds.length} slots via Playlist backlink`);
      const results = [];
      for (let i = 0; i < slotIds.length; i += 50) {
        const batch = slotIds.slice(i, i + 50);
        const orClauses = batch.map(id => `RECORD_ID()="${id}"`).join(",");
        const batchResults = await fetchAll(TABLES.playlistSlots, {
          filterByFormula: `OR(${orClauses})`,
        });
        results.push(...batchResults);
      }
      if (results.length > 0) return results;
    }
  } catch (e) {
    // Backlink field doesn't exist — fall through
  }

  // Strategy 4 (LAST RESORT): Fetch ALL slots and filter client-side.
  // This is extremely slow on large tables — should only hit if all above fail.
  console.warn(`[getPlaylistSlots] ⚠ Falling back to FULL TABLE fetch for ${playlistId} — all server-side strategies failed`);
  try {
    const allSlots = await fetchAll(TABLES.playlistSlots);
    const filtered = allSlots.filter(r => {
      const linked = r.fields["Playlist"];
      return Array.isArray(linked) && linked.includes(playlistId);
    });
    console.log(`[getPlaylistSlots] Client-side filter: ${filtered.length}/${allSlots.length} slots match`);
    return filtered;
  } catch (e) {
    console.error(`[getPlaylistSlots] Full fetch failed:`, e.message);
    return [];
  }
}

export async function getPlaylistsByStatus(status) {
  return fetchAll(TABLES.playlists, {
    filterByFormula: `{Status}="${status}"`,
  });
}

export async function getPlaylist(recordId) {
  return fetchOne(TABLES.playlists, recordId);
}

export async function createPlaylist(data) {
  return createRecord(TABLES.playlists, data);
}

export async function updatePlaylist(recordId, data) {
  return updateRecord(TABLES.playlists, recordId, data);
}

export async function deletePlaylist(recordId) {
  if (!TABLES.playlists || !recordId) return null;
  return deleteRecord(TABLES.playlists, recordId);
}

export async function approvePlaylist(recordId, approvedBy) {
  return updateRecord(TABLES.playlists, recordId, {
    'Status': 'Approved',
    'Approved At': new Date().toISOString(),
    'Approved By': approvedBy,
  });
}

export async function lockPlaylist(recordId) {
  return updateRecord(TABLES.playlists, recordId, {
    'Status': 'Locked',
    'Locked At': new Date().toISOString(),
  });
}

export async function markPlaylistSynced(recordId) {
  return updateRecord(TABLES.playlists, recordId, {
    'Synced to Operator': true,
    'Synced At': new Date().toISOString(),
  });
}

// ============================================================
// FIXTURES (Read-only, from existing table)
// ============================================================
export async function getFixtures(options = {}) {
  return fetchAll(TABLES.fixtures, options);
}

/**
 * Fetch fixtures with cellFormat=string so linked record fields (Round, Venue, Teams)
 * resolve to display text instead of record IDs.
 */
export async function getFixturesAsStrings() {
  if (!TABLES.fixtures) return [];
  const records = [];
  let offset = null;
  do {
    let url = `/${TABLES.fixtures}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    if (offset) url += `&offset=${offset}`;
    const data = await airtableFetch(url);
    if (data?.records) records.push(...data.records);
    offset = data?.offset || null;
  } while (offset);
  return records;
}

// Venue alias map — maps abbreviations ↔ full names for fixture matching
const VENUE_ALIASES = {
  'SCG': ['SCG', 'Sydney Cricket Ground'],
  'Sydney Cricket Ground': ['SCG', 'Sydney Cricket Ground'],
  'MCG': ['MCG', 'Melbourne Cricket Ground'],
  'Melbourne Cricket Ground': ['MCG', 'Melbourne Cricket Ground'],
  'Marvel': ['Marvel', 'Marvel Stadium', 'Docklands'],
  'Marvel Stadium': ['Marvel', 'Marvel Stadium', 'Docklands'],
  'KPST': ['KPST', 'GMHBA', 'GMHBA Stadium', 'Kardinia Park'],
  'GMHBA Stadium': ['KPST', 'GMHBA', 'GMHBA Stadium', 'Kardinia Park'],
  'Kardinia Park': ['KPST', 'GMHBA', 'GMHBA Stadium', 'Kardinia Park'],
};

/**
 * Build an Airtable FIND formula that matches any known alias for a venue name.
 * e.g. "SCG" → OR(FIND("SCG", ...), FIND("Sydney Cricket Ground", ...))
 */
function venueMatchFormula(venueName) {
  const aliases = VENUE_ALIASES[venueName] || [venueName];
  if (aliases.length === 1) {
    return `FIND("${aliases[0]}", ARRAYJOIN({Venue}))`;
  }
  return `OR(${aliases.map(a => `FIND("${a}", ARRAYJOIN({Venue}))`).join(',')})`;
}

export async function getFixturesByVenue(venueId, competition) {
  // Use cellFormat=string so linked record fields resolve to display text
  const venueFindFormula = venueMatchFormula(venueId);
  let allRecords = [];
  let offset = null;
  do {
    let url = `/${TABLES.fixtures}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    url += `&filterByFormula=${encodeURIComponent(`AND(${venueFindFormula}, {Competition}="${competition}")`)}`;
    url += `&sort[0][field]=Date&sort[0][direction]=asc`;
    if (offset) url += `&offset=${offset}`;
    const data = await airtableFetch(url);
    allRecords = [...allRecords, ...(data.records || [])];
    offset = data.offset;
  } while (offset);
  return allRecords;
}

export async function getFixturesByRounds(venueId, competition, rounds) {
  // If no rounds or "All" selected, get all fixtures for venue
  if (!rounds || rounds.length === 0 || rounds.includes('All')) {
    return getFixturesByVenue(venueId, competition);
  }
  
  // Rounds may be record IDs (from linked record fields fetched without cellFormat=string)
  // or display names (like "R1", "OR"). Detect which we have.
  const looksLikeRecordIds = rounds.every(r => typeof r === 'string' && r.startsWith('rec'));
  
  if (looksLikeRecordIds) {
    // Round values are Airtable record IDs — can't use cellFormat=string for matching
    // because it resolves {Round} to display names. Instead, fetch all venue fixtures
    // then client-side filter by round record IDs.
    console.log(`[getFixturesByRounds] Rounds are record IDs (${rounds.join(', ')}) — fetching venue fixtures then filtering client-side`);
    
    // Step 1: Get all fixtures for venue+competition (using cellFormat=string for venue name matching)
    const allVenueFixtures = await getFixturesByVenue(venueId, competition);
    
    // Step 2: Re-fetch the SAME fixtures WITHOUT cellFormat=string so Round field is raw record IDs
    const roundIdSet = new Set(rounds);
    const matchingFixtures = [];
    
    // Batch-fetch raw fixture records to check Round record IDs
    for (let i = 0; i < allVenueFixtures.length; i += 100) {
      const batch = allVenueFixtures.slice(i, i + 100);
      const idFormulas = batch.map(f => `RECORD_ID()="${f.id}"`).join(',');
      let url = `/${TABLES.fixtures}?pageSize=100&filterByFormula=${encodeURIComponent(`OR(${idFormulas})`)}`;
      const data = await airtableFetch(url);
      for (const rec of (data.records || [])) {
        const fixtureRounds = rec.fields["Round"] || [];
        const roundArr = Array.isArray(fixtureRounds) ? fixtureRounds : [fixtureRounds];
        if (roundArr.some(r => roundIdSet.has(r))) {
          // Return the cellFormat=string version for consistent data
          const displayVersion = allVenueFixtures.find(f => f.id === rec.id);
          if (displayVersion) matchingFixtures.push(displayVersion);
        }
      }
    }
    
    console.log(`[getFixturesByRounds] Matched ${matchingFixtures.length} fixture(s) from ${allVenueFixtures.length} venue fixtures`);
    return matchingFixtures;
  }
  
  // Round values are display names — use cellFormat=string formula matching directly
  const venueFindFormula = venueMatchFormula(venueId);
  const roundFormulas = rounds.map(r => `{Round}="${r}"`).join(',');
  let allRecords = [];
  let offset = null;
  do {
    let url = `/${TABLES.fixtures}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    url += `&filterByFormula=${encodeURIComponent(`AND(${venueFindFormula}, {Competition}="${competition}", OR(${roundFormulas}))`)}`;
    url += `&sort[0][field]=Date&sort[0][direction]=asc`;
    if (offset) url += `&offset=${offset}`;
    const data = await airtableFetch(url);
    allRecords = [...allRecords, ...(data.records || [])];
    offset = data.offset;
  } while (offset);
  return allRecords;
}

export async function getFixture(recordId) {
  return fetchOne(TABLES.fixtures, recordId);
}

/**
 * Get unique rounds for a competition/season, derived from fixtures.
 * Returns synthetic "round records" with fields: Round Name, Round Number, Competition, Season.
 * @param {string} competition - e.g. "AFL"
 * @param {string} season - e.g. "2026"
 */
export async function getRoundsByCompetition(competition, season) {
  // We need both record IDs (for linked-record writes) and display names (for UI).
  // Fetch fixtures twice: once normal (for Round record IDs), once cellFormat=string (for display names).
  
  const fetchFixtures = async (cellFormatString) => {
    let all = [];
    let offset = null;
    do {
      let url = `/${TABLES.fixtures}?pageSize=100`;
      if (cellFormatString) url += `&cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au`;
      url += `&filterByFormula=${encodeURIComponent(`{Competition}="${competition}"`)}`;
      url += `&fields[]=${encodeURIComponent("Round")}`;
      if (cellFormatString) url += `&sort[0][field]=Date&sort[0][direction]=asc`;
      if (offset) url += `&offset=${offset}`;
      const data = await airtableFetch(url);
      all = [...all, ...(data.records || [])];
      offset = data.offset;
    } while (offset);
    return all;
  };

  // Parallel fetch: raw IDs + display names
  const [rawFixtures, namedFixtures] = await Promise.all([
    fetchFixtures(false),
    fetchFixtures(true),
  ]);

  // Build map: fixture record ID → { roundRecordId, roundName }
  const fixtureMap = new Map();
  for (const f of rawFixtures) {
    const roundIds = f.fields["Round"];
    const roundId = Array.isArray(roundIds) ? roundIds[0] : roundIds;
    if (roundId) fixtureMap.set(f.id, { roundRecordId: roundId });
  }
  for (const f of namedFixtures) {
    const roundName = f.fields["Round"] || "";
    const entry = fixtureMap.get(f.id);
    if (entry && roundName) entry.roundName = roundName;
  }

  // Extract unique rounds by record ID
  const seen = new Map();
  let orderIndex = 0;
  for (const entry of fixtureMap.values()) {
    if (!entry.roundRecordId || !entry.roundName || seen.has(entry.roundRecordId)) continue;
    const name = entry.roundName;
    seen.set(entry.roundRecordId, {
      id: entry.roundRecordId, // Real Airtable record ID
      fields: {
        "Round Name": name,
        "Round Number": name === "OR" ? 0 : parseInt(name.replace(/\D/g, ""), 10) || (90 + orderIndex),
        "Competition": competition,
        "Season": season,
      },
    });
    orderIndex++;
  }

  return [...seen.values()].sort(
    (a, b) => a.fields["Round Number"] - b.fields["Round Number"]
  );
}

/**
 * Get venue fixture rounds using cellFormat=string so Round resolves to display names.
 * Used by AddBookingLineModal to filter the rounds list to only those with fixtures at the venue.
 */
export async function getVenueFixtureRounds(venueName, competition) {
  let allFixtures = [];
  let offset = null;
  do {
    let url = `/${TABLES.fixtures}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    url += `&filterByFormula=${encodeURIComponent(`AND(FIND("${venueName}", ARRAYJOIN({Venue})), {Competition}="${competition}")`)}`;
    url += `&fields[]=${encodeURIComponent("Round")}`;
    if (offset) url += `&offset=${offset}`;
    const data = await airtableFetch(url);
    allFixtures = [...allFixtures, ...(data.records || [])];
    offset = data.offset;
  } while (offset);

  return new Set(allFixtures.map(f => f.fields["Round"] || "").filter(Boolean));
}

/**
 * Fetch home rounds for a club at TVC venues (MCG + Marvel).
 * Uses cellFormat=string so linked record fields resolve to display text.
 * @param {string} clubName - e.g. "St Kilda", "Collingwood"
 * @returns {Promise<string[]>} Sorted round labels, e.g. ["OR", "R3", "R7", "R10"]
 */
export async function getClubHomeRounds(clubName) {
  if (!clubName || !TABLES.fixtures) return [];
  try {
    const records = [];
    let offset = null;
    do {
      let url = `/${TABLES.fixtures}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
      if (offset) url += `&offset=${offset}`;
      const data = await airtableFetch(url);
      if (data?.records) records.push(...data.records);
      offset = data?.offset || null;
    } while (offset);

    const cn = clubName.toLowerCase();
    const tvcVenues = ["mcg", "marvel stadium", "marvel"];
    const rounds = new Set();

    records.forEach((r) => {
      const f = r.fields;
      const homeTeam = (f["Home Team"] || "").toLowerCase();
      const venue = (f["Venue"] || f["Venue Name"] || "").toLowerCase();

      // Check home team matches club
      if (!homeTeam.includes(cn) && !cn.includes(homeTeam.split(" ")[0])) return;
      // Check venue is MCG or Marvel
      if (!tvcVenues.some((v) => venue.includes(v))) return;

      // Extract round
      const round = f["Round Name"] || f["Round"] || f["Round Label"] || "";
      if (round && !round.startsWith("rec")) {
        rounds.add(round.trim().toUpperCase());
      }
    });

    // Sort: OR first, then R1, R2, ... R24
    const sorted = Array.from(rounds).sort((a, b) => {
      if (a === "OR") return -1;
      if (b === "OR") return 1;
      const na = parseInt(a.replace(/\D/g, "")) || 0;
      const nb = parseInt(b.replace(/\D/g, "")) || 0;
      return na - nb;
    });

    console.log(`[commercialAirtable] getClubHomeRounds(${clubName}): ${sorted.length} rounds at MCG/Marvel →`, sorted);
    return sorted;
  } catch (err) {
    console.error("[commercialAirtable] getClubHomeRounds failed:", err.message);
    return [];
  }
}

// ============================================================
// PARTNERS (Read-only, from existing table)
// ============================================================
export async function getPartners(options = {}) {
  return fetchAll(TABLES.partners, {
    sort: [{ field: 'Name', direction: 'asc' }],
    ...options,
  });
}

export async function getPartnersByType(type) {
  return fetchAll(TABLES.partners, {
    filterByFormula: `{Partner Type}="${type}"`,
    sort: [{ field: 'Name', direction: 'asc' }],
  });
}

// ============================================================
// VENUES (Read-only, from existing table)
// ============================================================
export async function getVenues(options = {}) {
  return fetchAll(TABLES.venues, {
    sort: [{ field: 'Name', direction: 'asc' }],
    ...options,
  });
}

export async function getVenuesByCompetition(competition) {
  return fetchAll(TABLES.venues, {
    filterByFormula: `OR({Competition}="${competition}", {Competition}="Both")`,
    sort: [{ field: 'Name', direction: 'asc' }],
  });
}

// ============================================================
// CONTENT LIBRARY / ASSETS (Read-only, from existing table)
// ============================================================
export async function getAssets(options = {}) {
  return fetchAll(TABLES.assets, options);
}

export async function getAssetsByClient(clientId) {
  return fetchAll(TABLES.assets, {
    filterByFormula: `FIND("${clientId}", ARRAYJOIN({Client}))`,
  });
}

export async function getApprovedAssets(clientId, inventoryType, duration) {
  let formula = `AND({Status}="Approved"`;
  
  if (clientId) formula += `, FIND("${clientId}", ARRAYJOIN({Client}))`;
  if (inventoryType) formula += `, {Inventory Type}="${inventoryType}"`;
  if (duration) formula += `, {Duration}="${duration}"`;
  
  formula += ')';
  
  return fetchAll(TABLES.assets, { filterByFormula: formula });
}

// ============================================================
// TVC REELS & SLOTS (Read-only for club view)
// ============================================================
export async function getTVCReels(options = {}) {
  if (!TABLES.tvcReels) {
    console.warn("[commercialAirtable] REACT_APP_TVC_REELS_TABLE_ID not set");
    return [];
  }
  // Use cellFormat=string so linked record fields (Home Team, Away Team, Venue, Round) resolve to display text
  let allRecords = [];
  let offset = null;
  const { filterByFormula, sort, maxRecords } = options;
  do {
    let url = `/${TABLES.tvcReels}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    if (filterByFormula) url += `&filterByFormula=${encodeURIComponent(filterByFormula)}`;
    if (sort && sort.length > 0) {
      sort.forEach((s, i) => {
        url += `&sort[${i}][field]=${encodeURIComponent(s.field)}&sort[${i}][direction]=${s.direction || "asc"}`;
      });
    }
    if (maxRecords) url += `&maxRecords=${maxRecords}`;
    if (offset) url += `&offset=${offset}`;
    const data = await airtableFetch(url);
    allRecords = [...allRecords, ...(data.records || [])];
    offset = data.offset;
  } while (offset);
  return allRecords;
}

export async function getAllTVCReelSlots(options = {}) {
  if (!TABLES.tvcReelSlots) {
    console.warn("[commercialAirtable] REACT_APP_TVC_REEL_SLOTS_TABLE_ID not set");
    return [];
  }
  return fetchAll(TABLES.tvcReelSlots, options);
}

export async function deleteTVCReelSlot(recordId) {
  if (!TABLES.tvcReelSlots) {
    throw new Error("REACT_APP_TVC_REEL_SLOTS_TABLE_ID not set");
  }
  return deleteRecord(TABLES.tvcReelSlots, recordId);
}

// ============================================================
// PLAYLIST SLOTS (LED commercial — club read-only view)
// ============================================================

/**
 * Fetch all playlist slots. Optionally filter by formula.
 */
export async function getAllPlaylistSlots(options = {}) {
  if (!TABLES.playlistSlots) {
    console.warn("[commercialAirtable] REACT_APP_PLAYLIST_SLOTS_TABLE_ID not set");
    return [];
  }
  return fetchAll(TABLES.playlistSlots, options);
}

/**
 * Clear multiple playlist slots back to filler (batch update).
 * Resets Client, Content Name, Content ID, Content Path, Slot Type, Tier, Category, Colour.
 * @param {string[]} slotIds - Array of Airtable record IDs to clear
 */
export async function clearPlaylistSlots(slotIds) {
  if (!TABLES.playlistSlots) {
    throw new Error("REACT_APP_PLAYLIST_SLOTS_TABLE_ID not set");
  }
  const clearFields = {
    "Client": "",
    "Content Name": "",
    "Content ID": "",
    "Content Path": "",
    "Slot Type": "filler",
    "Tier": "",
    "Category": "",
    "Colour": "",
  };
  // Batch in groups of 10 (Airtable limit)
  for (let i = 0; i < slotIds.length; i += 10) {
    const batch = slotIds.slice(i, i + 10);
    const records = batch.map(id => ({ id, fields: clearFields }));
    await fetch(
      `https://api.airtable.com/v0/${process.env.REACT_APP_COMMERCIAL_BASE_ID}/${TABLES.playlistSlots}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${COMMERCIAL_PAT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ records }),
      }
    ).then(res => {
      if (!res.ok) throw new Error(`Batch clear failed (${res.status})`);
      return res.json();
    });
  }
}

/**
 * Delete playlist slot records entirely (used for bonus/run-on slots that were created, not filled).
 * @param {string[]} slotIds - Array of Airtable record IDs to delete
 */
export async function deletePlaylistSlots(slotIds) {
  if (!TABLES.playlistSlots || slotIds.length === 0) return;
  // Airtable allows up to 10 deletes per request
  for (let i = 0; i < slotIds.length; i += 10) {
    const batch = slotIds.slice(i, i + 10);
    const params = batch.map(id => `records[]=${id}`).join("&");
    await fetch(
      `https://api.airtable.com/v0/${process.env.REACT_APP_COMMERCIAL_BASE_ID}/${TABLES.playlistSlots}?${params}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${COMMERCIAL_PAT}` },
      }
    ).then(res => {
      if (!res.ok) throw new Error(`Batch delete failed (${res.status})`);
      return res.json();
    });
  }
}

/**
 * Delete a single playlist slot record.
 * @param {string} slotId - Airtable record ID to delete
 */
export async function deletePlaylistSlot(slotId) {
  if (!TABLES.playlistSlots || !slotId) return null;
  return deleteRecord(TABLES.playlistSlots, slotId);
}

/**
 * Update a single playlist slot record.
 * @param {string} slotId - Airtable record ID
 * @param {Object} fields - Fields to update
 */
export async function updatePlaylistSlot(slotId, fields) {
  if (!TABLES.playlistSlots || !slotId) return null;
  return updateRecord(TABLES.playlistSlots, slotId, fields);
}

/**
 * Delete ALL playlist slot records for a given playlist.
 * Fetches slot IDs first, then batch-deletes them.
 * Used by reshufflePlaylist to clear and rebuild.
 * @param {string} playlistId - Airtable record ID of the playlist
 */
export async function deletePlaylistSlotsByPlaylist(playlistId) {
  if (!TABLES.playlistSlots || !playlistId) return;
  const slots = await getPlaylistSlotsByPlaylist(playlistId);
  if (slots.length === 0) return;
  const ids = slots.map(s => s.id);
  await deletePlaylistSlots(ids);
  console.log(`[commercialAirtable] deletePlaylistSlotsByPlaylist: deleted ${ids.length} slots for playlist ${playlistId}`);
}

/**
 * Create playlist slot records (sequential, batched in groups of 10).
 * @param {Object[]} records - Array of field objects to create
 * @returns {Object[]} Created Airtable records
 */
export async function createPlaylistSlots(records) {
  if (!TABLES.playlistSlots || records.length === 0) return [];
  return createRecords(TABLES.playlistSlots, records);
}

/**
 * Create playlist slot records in parallel (batched in groups of 10, all batches sent concurrently).
 * Used by generatePlaylist for performance when creating 100+ slots.
 * @param {Object[]} records - Array of field objects to create
 * @returns {Object[]} Created Airtable records
 */
export async function createPlaylistSlotsParallel(records) {
  if (!TABLES.playlistSlots || records.length === 0) return [];
  const chunks = [];
  for (let i = 0; i < records.length; i += 10) {
    chunks.push(records.slice(i, i + 10));
  }
  // Run all chunks in parallel (max ~10 concurrent batches = 100 records)
  const results = await Promise.all(
    chunks.map(chunk =>
      airtableFetch(`/${TABLES.playlistSlots}`, {
        method: 'POST',
        body: JSON.stringify({ records: chunk.map(fields => ({ fields })) }),
      }).then(data => data.records || [])
    )
  );
  return results.flat();
}

/**
 * @param {string[]} slotIds - Airtable record IDs of playlist slots
 * @param {string} status - "Approved" | "Rejected" | "Pending"
 */
export async function updatePlaylistSlotStatus(slotIds, status) {
  if (!TABLES.playlistSlots || slotIds.length === 0) return;
  const updates = slotIds.map(id => ({ id, fields: { "Content Status": status } }));
  for (let i = 0; i < updates.length; i += 10) {
    const batch = updates.slice(i, i + 10);
    try {
      await airtableFetch(`/${TABLES.playlistSlots}`, {
        method: "PATCH",
        body: JSON.stringify({ records: batch }),
      });
    } catch (batchErr) {
      console.warn("[commercialAirtable] Batch LED status update failed, trying individually:", batchErr.message);
      for (const rec of batch) {
        try {
          await airtableFetch(`/${TABLES.playlistSlots}`, {
            method: "PATCH",
            body: JSON.stringify({ records: [rec] }),
          });
        } catch (singleErr) {
          console.warn(`[commercialAirtable] LED slot ${rec.id} update failed (may be deleted):`, singleErr.message);
        }
      }
    }
  }
}

/**
 * Update Content Status on TVC reel slot(s) — used by operator approval view.
 * @param {string[]} slotIds - Airtable record IDs of TVC reel slots
 * @param {string} status - "Approved" | "Rejected" | "Pending"
 */
export async function updateTVCReelSlotStatus(slotIds, status) {
  if (!TABLES.tvcReelSlots || slotIds.length === 0) return;
  const updates = slotIds.map(id => ({ id, fields: { "Content Status": status } }));
  for (let i = 0; i < updates.length; i += 10) {
    const batch = updates.slice(i, i + 10);
    try {
      await airtableFetch(`/${TABLES.tvcReelSlots}`, {
        method: "PATCH",
        body: JSON.stringify({ records: batch }),
      });
    } catch (batchErr) {
      // Batch failed (e.g. one stale record ID) — fall back to individual updates
      console.warn("[commercialAirtable] Batch status update failed, trying individually:", batchErr.message);
      for (const rec of batch) {
        try {
          await airtableFetch(`/${TABLES.tvcReelSlots}`, {
            method: "PATCH",
            body: JSON.stringify({ records: [rec] }),
          });
        } catch (singleErr) {
          console.warn(`[commercialAirtable] Slot ${rec.id} update failed (may be deleted):`, singleErr.message);
        }
      }
    }
  }
}

/**
 * Fetch a fixture's round as a display string.
 * Uses cellFormat=string so linked record fields return text instead of record IDs.
 * @param {string} fixtureId - Airtable record ID of the fixture
 * @returns {Promise<string>} Round label (e.g. "OR", "R3") or ""
 */
export async function getFixtureRound(fixtureId) {
  if (!fixtureId || !TABLES.fixtures) return "";
  try {
    // cellFormat=string requires timeZone — makes linked record fields return display text
    const data = await airtableFetch(`/${TABLES.fixtures}/${fixtureId}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au`);
    const f = data?.fields || {};
    console.log(`[commercialAirtable] getFixtureRound(${fixtureId}) fields:`, Object.keys(f).join(", "));
    console.log(`[commercialAirtable] getFixtureRound raw values:`, {
      "Round Name": f["Round Name"],
      "Round": f["Round"],
      "Round Label": f["Round Label"],
      "Round Number": f["Round Number"],
      "Match ID": f["Match ID"],
      "Name": f["Name"],
    });
    // Try direct round fields first
    let round = f["Round Name"] || f["Round"] || f["Round Label"] || "";
    // If still empty, try extracting from Match ID (e.g. "26-AFL-STK-01" → seq 01 = OR)
    if (!round && f["Match ID"]) {
      const mid = f["Match ID"];
      // Match ID doesn't encode round directly, but Name or Fixture ID might
      const rmatch = mid.match(/\bOR\b/i) || mid.match(/\bR(\d+)\b/i);
      if (rmatch) round = rmatch[0].toUpperCase();
    }
    // Try Round Number (0 = OR)
    if (!round && f["Round Number"] !== undefined && f["Round Number"] !== null && f["Round Number"] !== "") {
      const rn = Number(f["Round Number"]);
      round = rn === 0 ? "OR" : "R" + rn;
    }
    return String(round).trim();
  } catch (err) {
    console.error("[commercialAirtable] getFixtureRound FAILED:", err.message, err);
    return "";
  }
}

// ============================================================
// CONTENT MASTER BASE (separate from commercial deals base)
// Used to update Round Allocations when clubs remove content from TVC playlists
// ============================================================
const CONTENT_BASE_ID = process.env.REACT_APP_CONTENT_BASE_ID || "appKEnvtQmLtNO17d";
const CONTENT_PAT = process.env.REACT_APP_COMMERCIAL_CONTENT_PAT || process.env.REACT_APP_CONTENT_PAT || COMMERCIAL_PAT;
const CONTENT_ASSETS_TABLE = process.env.REACT_APP_CONTENT_ASSETS_TABLE_ID || "tbl42umFS2cksn1UE";

async function contentBaseFetch(endpoint, options = {}) {
  if (!CONTENT_BASE_ID || !CONTENT_PAT) {
    console.error("[commercialAirtable] Content base not configured — CONTENT_BASE_ID:", !!CONTENT_BASE_ID, "CONTENT_PAT:", !!CONTENT_PAT);
    return null;
  }
  const pat = CONTENT_PAT.split("").filter(c => c.charCodeAt(0) < 128).join("").trim();
  const url = `https://api.airtable.com/v0/${CONTENT_BASE_ID}${endpoint}`;
  console.log("[commercialAirtable] contentBaseFetch:", options.method || "GET", endpoint);
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${pat}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.text();
    console.error('[commercialAirtable] Content base error:', error);
    throw new Error(`Content base error: ${response.status}`);
  }
  return response.json();
}

/**
 * Strip a round allocation from a content record's Round Allocations JSON.
 * Reads current value, removes matching round + type, writes back.
 * @param {string} contentRecordId - Airtable record ID in content master base
 * @param {string} roundToRemove - e.g. "OR", "R3", "3"
 * @param {string} [typeToRemove] - "paid", "bonus", or null to remove all for this round
 */
export async function removeRoundFromContent(contentRecordId, roundToRemove, typeToRemove = null) {
  if (!contentRecordId || !roundToRemove) return;

  // Safety: ensure we have a string record ID
  const recordId = Array.isArray(contentRecordId) ? contentRecordId[0] : String(contentRecordId);
  if (!recordId || !recordId.startsWith("rec")) {
    console.warn("[commercialAirtable] removeRoundFromContent: invalid record ID:", contentRecordId);
    return;
  }

  console.log(`[commercialAirtable] removeRoundFromContent: record=${recordId}, round=${roundToRemove}, type=${typeToRemove || "all"}`);

  // Read current record
  const record = await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}/${recordId}`);
  if (!record) return;

  const raw = record.fields?.["Round Allocations"] || "";
  let allocations = [];
  try {
    allocations = JSON.parse(raw);
    if (!Array.isArray(allocations)) return;
  } catch { return; }

  // Normalise round labels to canonical form: "Round 3" → "R3", "Opening Round" → "OR", "3" → "R3", "R3" → "R3"
  const normalise = (r) => {
    const s = String(r || "").trim().toUpperCase();
    if (s === "OR" || s === "0" || s === "OPENING ROUND") return "OR";
    const digits = s.replace(/^(?:ROUND\s*|R)/, "");
    if (/^\d+$/.test(digits)) return "R" + digits;
    return s;
  };
  const target = normalise(roundToRemove);
  let found = false;
  const updated = allocations.filter((a) => {
    const r = normalise(a.round || a.Round || "");
    if (r !== target) return true; // different round — keep
    if (typeToRemove && !found) {
      // Type-specific removal: only remove the first matching type
      const allocType = (a.type || "paid").toLowerCase();
      if (allocType === typeToRemove.toLowerCase()) {
        found = true;
        return false; // remove this one
      }
      return true; // different type — keep
    }
    return false; // no type filter or already matched — remove all for this round
  });

  console.log(`[commercialAirtable] Stripping round ${roundToRemove}${typeToRemove ? ` (${typeToRemove})` : ""} from content ${recordId}: ${allocations.length} → ${updated.length} allocations`);

  // Write back
  await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}`, {
    method: "PATCH",
    body: JSON.stringify({
      records: [{ id: recordId, fields: { "Round Allocations": JSON.stringify(updated) } }],
    }),
  });
}

/**
 * Fetch all content records with Round Allocations populated.
 * Returns a map keyed by multiple identifiers (lumenId, contentName, fileName)
 * → { roundAllocations (JSON string), contentRecordId }
 * Used by club gallery to cross-reference round data from commercial content base.
 */
/**
 * Fetch a map of Lumen8 ID → { assetId, destination } from the content master base.
 * Used by LED and TVC playlist views to write correct Asset IDs and determine Club/Commercial source.
 * @returns {Object} Map of { "STK_3PH08KK": { assetId: "COM-283", destination: "MCG-tiled-commercial" }, ... }
 */
export async function fetchLumen8ToAssetIdMap() {
  const map = {};
  let offset = null;
  do {
    const params = new URLSearchParams({ pageSize: "100" });
    if (offset) params.set("offset", offset);
    const fieldParams = ["Lumen8+ID", "Asset+ID", "Asset+Destination", "Duration"]
      .map(f => `fields%5B%5D=${f}`).join("&");
    const url = `/${CONTENT_ASSETS_TABLE}?${params.toString()}&${fieldParams}`;
    const data = await contentBaseFetch(url);
    if (!data || !data.records) break;
    data.records.forEach((r) => {
      const lumen8Id = (r.fields?.["Lumen8 ID"] || "").trim();
      const assetId = (r.fields?.["Asset ID"] || "").trim();
      const destination = (r.fields?.["Asset Destination"] || "").trim();
      const duration = r.fields?.["Duration"] || "";
      if (lumen8Id) {
        map[lumen8Id] = { assetId, destination, duration };
      }
    });
    offset = data.offset || null;
  } while (offset);
  console.log(`[commercialAirtable] fetchLumen8ToAssetIdMap: ${Object.keys(map).length} mappings`);
  return map;
}

// ✅ PERF: Cache round allocations — they change rarely (set up before the season)
let _raMapCache = null;
let _raMapCacheTime = 0;
const RA_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export async function fetchRoundAllocationsMap() {
  // Return cached if still fresh
  if (_raMapCache && (Date.now() - _raMapCacheTime) < RA_CACHE_TTL_MS) {
    console.log(`[commercialAirtable] fetchRoundAllocationsMap: returning cached (${Object.keys(_raMapCache).length} keys)`);
    return _raMapCache;
  }

  const map = {};
  let offset = null;
  do {
    const params = new URLSearchParams({
      filterByFormula: 'NOT({Round Allocations} = "")',
      pageSize: "100",
    });
    // Fetch name fields for cross-referencing
    const fieldParams = ["Lumen8+ID", "Round+Allocations", "File%2FFolder+Name"]
      .map(f => `fields%5B%5D=${f}`).join("&");
    const url = `/${CONTENT_ASSETS_TABLE}?${params.toString()}&${fieldParams}`;
    if (offset) params.set("offset", offset);
    const finalUrl = offset
      ? `/${CONTENT_ASSETS_TABLE}?${params.toString()}&${fieldParams}`
      : url;
    const data = await contentBaseFetch(finalUrl);
    if (!data || !data.records) break;
    data.records.forEach((r) => {
      const ra = r.fields?.["Round Allocations"] || "";
      if (!ra) return;
      const entry = { roundAllocations: ra, contentRecordId: r.id };
      // Index by every non-empty identifier so gallery can match by any of them
      const keys = [
        (r.fields?.["Lumen8 ID"] || "").trim(),
        (r.fields?.["File/Folder Name"] || "").trim(),
      ].filter(Boolean);
      for (const key of keys) {
        if (!map[key]) map[key] = entry; // first-match wins
      }
    });
    offset = data.offset || null;
  } while (offset);
  console.log(`[commercialAirtable] fetchRoundAllocationsMap: ${Object.keys(map).length} index keys`);
  _raMapCache = map;
  _raMapCacheTime = Date.now();
  return map;
}

/**
 * Look up a content record in the content master base by Lumen8 ID, file name, or content name.
 * Searches multiple fields to maximise match chance for older content.
 * Returns the Airtable record ID if found, null otherwise.
 * @param {string} searchTerm - e.g. "COM-275" or "STK_1EK5983_CMC_MCG-tiled"
 * @returns {string|null} Airtable record ID
 */
export async function lookupContentRecordByLumenId(searchTerm) {
  if (!searchTerm) return null;
  // Escape double quotes in the search term for Airtable formula safety
  const safe = searchTerm.replace(/"/g, '\\"');
  // Search across multiple name/ID fields — any one matching is enough
  const formula = `OR({Lumen8 ID}="${safe}",{File/Folder Name}="${safe}")`;
  const params = new URLSearchParams({
    filterByFormula: formula,
    maxRecords: "1",
    pageSize: "1",
  });
  const url = `/${CONTENT_ASSETS_TABLE}?${params.toString()}&fields%5B%5D=Lumen8+ID`;
  const data = await contentBaseFetch(url);
  if (!data?.records?.length) {
    console.warn(`[commercialAirtable] lookupContentRecord: "${searchTerm}" → not found`);
    return null;
  }
  console.log(`[commercialAirtable] lookupContentRecord: "${searchTerm}" → ${data.records[0].id}`);
  return data.records[0].id;
}

/**
 * Update Round Allocations JSON on a content record in the commercial content master base.
 * @param {string} contentRecordId - Airtable record ID
 * @param {Array} allocations - Array of {round, numberOfRotations} objects
 */
export async function updateContentRoundAllocations(contentRecordId, allocations) {
  if (!contentRecordId) throw new Error("Content record ID required");
  const recordId = Array.isArray(contentRecordId) ? contentRecordId[0] : String(contentRecordId);
  const jsonStr = JSON.stringify(allocations);
  console.log(`[commercialAirtable] updateContentRoundAllocations: ${recordId} → ${jsonStr}`);
  await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}`, {
    method: "PATCH",
    body: JSON.stringify({
      records: [{ id: recordId, fields: { "Round Allocations": jsonStr } }],
    }),
  });
}

/**
 * Create a new content record in the commercial content master base.
 * Used when a gallery asset doesn't yet have a corresponding record
 * (e.g. older club uploads that predate the commercial content flow).
 * @param {object} asset - Asset info from gallery { lumenId, title, destination }
 * @returns {string} New Airtable record ID
 */
export async function createContentRecord(asset) {
  const fields = {};
  // Lumen8 ID is a computed field — cannot be set directly
  // File/Folder Name is the writable identifier field
  if (asset.title) fields["File/Folder Name"] = asset.title;
  
  console.log(`[commercialAirtable] createContentRecord:`, fields);
  const data = await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}`, {
    method: "POST",
    body: JSON.stringify({ records: [{ fields }] }),
  });
  if (!data?.records?.[0]?.id) {
    throw new Error("Failed to create content record");
  }
  const newId = data.records[0].id;
  console.log(`[commercialAirtable] ✅ Created content record: ${newId}`);
  return newId;
}

/**
 * Fetch thumbnail URLs and preview URLs for a set of content record IDs.
 * Returns a map of recordId → { thumbUrl, previewUrl }
 * @param {string[]} recordIds - Airtable record IDs from content master base
 */
export async function fetchContentPreviews(recordIds) {
  if (!recordIds || recordIds.length === 0) return {};
  const map = {};
  // Batch fetch — Airtable supports up to 100 per formula OR
  const BATCH = 50;
  for (let i = 0; i < recordIds.length; i += BATCH) {
    const batch = recordIds.slice(i, i + BATCH);
    const orClauses = batch.map(id => `RECORD_ID()="${id}"`).join(",");
    const params = `filterByFormula=OR(${encodeURIComponent(orClauses)})&fields%5B%5D=Asset+Thumbnails&fields%5B%5D=Asset+Preview+URL&fields%5B%5D=Asset+Download+URL+(raw)&fields%5B%5D=Lumen8+ID`;
    const data = await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}?${params}`);
    if (data?.records) {
      data.records.forEach((r) => {
        const f = r.fields;
        const thumbs = f["Asset Thumbnails"];
        const thumbUrl = Array.isArray(thumbs) && thumbs.length > 0
          ? (thumbs[0].thumbnails?.large?.url || thumbs[0].thumbnails?.small?.url || thumbs[0].url || "")
          : "";
        const previewUrl = f["Asset Preview URL"] || "";
        const videoUrl = f["Asset Download URL (raw)"] || "";
        map[r.id] = { thumbUrl, previewUrl, videoUrl };
      });
    }
  }
  console.log(`[commercialAirtable] fetchContentPreviews: ${Object.keys(map).length}/${recordIds.length} records with thumbnails`);
  return map;
}

// ============================================================
// AUTO-FILL: Pull ALL club content into commercial LED playlists

/**
 * Fetch Grouped Asset Links from the Content Master base for tiled/360 LED assets.
 * Returns a map of Lumen8 ID → ribbonItems array (each: { name, downloadUrl }).
 * Used by the Operator View to enable tiled 3D and 360° LED previews.
 * 
 * @param {string[]} lumen8Ids - Array of Lumen8 IDs to look up (e.g. ["COL_NA1RQ46", "COL_PZEV51K"])
 * @returns {Object} Map of { "COL_NA1RQ46": [{ name: "..._FOP-1.mp4", downloadUrl: "https://..." }, ...], ... }
 */
export async function fetchGroupedAssetLinks(lumen8Ids) {
  if (!lumen8Ids || lumen8Ids.length === 0) return {};
  const result = {};
  const BATCH = 20;
  
  for (let i = 0; i < lumen8Ids.length; i += BATCH) {
    const batch = lumen8Ids.slice(i, i + BATCH);
    const orClauses = batch.map(id => `{Lumen8 ID}="${id}"`).join(",");
    const fields = ["Lumen8+ID", "Grouped+Asset+Links"].map(f => `fields%5B%5D=${f}`).join("&");
    const url = `/${CONTENT_ASSETS_TABLE}?filterByFormula=OR(${encodeURIComponent(orClauses)})&${fields}`;
    
    try {
      const data = await contentBaseFetch(url);
      if (!data?.records) continue;
      
      for (const r of data.records) {
        const lumen8Id = (r.fields?.["Lumen8 ID"] || "").trim();
        const raw = r.fields?.["Grouped Asset Links"] || "";
        if (!lumen8Id || !raw) continue;
        
        let items = [];
        
        // Try JSON parse first (field may contain JSON array of { name, url, downloadUrl })
        if (typeof raw === "string" && raw.trim().startsWith("[")) {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              const strip = (s) => (s || "").replace(/^_+/, "").replace(/_+$/, "").trim();
              items = parsed
                .filter(item => item && (item.downloadUrl || item.url))
                .map(item => ({
                  name: item.name || "",
                  downloadUrl: strip(item.downloadUrl) || strip(item.url),
                }));
            }
          } catch { /* not JSON, try other formats */ }
        }
        
        // Fallback: comma/newline separated URLs or markdown links
        if (items.length === 0 && typeof raw === "string" && raw.trim()) {
          items = raw.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
            .map(entry => {
              const mdMatch = entry.match(/\[(.*?)\]\((.*?)\)/);
              if (mdMatch) return { name: mdMatch[1], downloadUrl: mdMatch[2] };
              const urlClean = entry.replace(/[?#].*/, "");
              const segments = urlClean.split("/").filter(Boolean);
              const name = decodeURIComponent(segments[segments.length - 1] || entry);
              return { name, downloadUrl: entry };
            })
            .filter(item => item.downloadUrl);
        }
        
        if (items.length > 0) {
          result[lumen8Id] = items;
        }
      }
    } catch (err) {
      console.warn('[commercialAirtable] Failed to fetch grouped asset links batch:', err.message);
    }
  }
  
  console.log(`[commercialAirtable] fetchGroupedAssetLinks: ${Object.keys(result).length}/${lumen8Ids.length} assets with level files`);
  return result;
}

// ============================================================
// AUTO-FILL: Pull ALL club content into commercial LED playlists
// Triggered manually by Lumen8 coordinators via "Auto Fill Club Content" button
// ============================================================

/**
 * Fetch all content with round allocations from the content base,
 * then seed each into the matching commercial playlist slots.
 * 
 * @param {string} [filterRound] - Optional: only seed for this round (e.g. "OR", "R1")
 * @param {string} [filterVenue] - Optional: only seed for this venue (e.g. "MCG", "Marvel Stadium")
 * @returns {Promise<{totalSeeded: number, totalSkipped: number, contentProcessed: number, errors: string[]}>}
 */
export async function autoFillAllClubContent(filterRound = null, filterVenue = null) {
  // ── DISABLED ── See autoSeedClubContent for rationale.
  // Club content placement goes through "Place Club Content" button after Commercial QA.
  console.log(`[autoFill] DISABLED — club content is placed via "Place Club Content" button after Commercial QA approval`);
  return { totalSeeded: 0, totalSkipped: 0, contentProcessed: 0, errors: [] };
}

// ============================================================
// AUTO-SEED: Push club content into commercial LED playlists
// Called from IngestFlow Gallery when clubs save round allocations
// ============================================================

/**
 * Fetch a full content record from the content master base.
 * Returns fields needed for auto-seeding into playlists.
 */
export async function fetchContentRecord(contentRecordId) {
  if (!contentRecordId) return null;
  const recordId = Array.isArray(contentRecordId) ? contentRecordId[0] : String(contentRecordId);
  // cellFormat=string resolves linked record fields (Venue, Uploaded By) to display text
  const data = await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}/${recordId}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au`);
  if (!data?.fields) return null;
  const f = data.fields;
  const g = (val) => Array.isArray(val) ? val[0] : val;
  console.log(`[fetchContentRecord] Fields for ${recordId}:`, JSON.stringify(f, null, 2));

  let venue = g(f["Venue"]) || "";
  const contentName = f["File/Folder Name"] || f["Original File Name"] || "";
  const destination = f["Asset Destination"] || "";

  // Derive venue from filename or destination if Venue field is empty
  // (common for older content or auto-created records)
  if (!venue) {
    const combined = `${contentName} ${destination}`.toLowerCase();
    if (combined.includes("mcg")) venue = "MCG";
    else if (combined.includes("marvel") || combined.includes("mrv")) venue = "Marvel Stadium";
    if (venue) console.log(`[fetchContentRecord] Derived venue "${venue}" from name/destination`);
  }

  return {
    recordId: data.id,
    contentName,
    client: g(f["Uploaded By"]) || "",
    venue,
    contentId: g(f["Asset ID"]) || g(f["Lumen8 ID"]) || "",
    filePath: f["Asset FilePath"] || "",
    destination,
    duration: f["Duration"] || "30",
    tier: f["Tier"] || "",
    category: f["Category"] || "",
    colour: f["Primary Colour"] || "",
  };
}

/**
 * Batch update playlist slots (write content into filler slots).
 * @param {Array<{id: string, fields: object}>} updates
 */
export async function batchUpdatePlaylistSlots(updates) {
  if (!TABLES.playlistSlots || updates.length === 0) return [];
  const results = [];
  for (let i = 0; i < updates.length; i += 10) {
    const batch = updates.slice(i, i + 10);
    const data = await airtableFetch(`/${TABLES.playlistSlots}`, {
      method: "PATCH",
      body: JSON.stringify({
        records: batch.map(u => ({ id: u.id, fields: u.fields })),
      }),
    });
    if (data.records) results.push(...data.records);
  }
  return results;
}

/**
 * Get playlist slots for a specific playlist.
 * Strategy 1: Filter by "Playlist Record ID" field (most reliable — contains actual record ID)
 * Strategy 2: Formula filter using link field names on Slots table
 * Strategy 3: Read slot IDs from Playlist record's "Playlist Slots" link field
 */
export async function getPlaylistSlotsByPlaylist(playlistId) {
  // Delegate to the robust getPlaylistSlots which has multi-strategy + client-side fallback
  return getPlaylistSlots(playlistId);
}

/** Normalise round string for matching: "R3", "Round 3", "3", "OR", "Opening Round", "0" → canonical form */
function normaliseRoundLabel(r) {
  if (!r) return "";
  const s = String(r).trim().toUpperCase();
  if (s === "0" || s === "OR" || s === "OPENING ROUND" || s === "R0") return "OR";
  const m = s.replace(/^(?:ROUND\s*|R)/, "");
  if (/^\d+$/.test(m)) return "R" + m;
  // Finals: QF, PF, SF, GF
  if (["QF", "PF", "SF", "GF"].includes(s)) return s;
  return s;
}

/**
 * Auto-seed club LED content into commercial playlists.
 *
 * Called after a club saves Round Allocations in IngestFlow Gallery.
 * For each round in the allocations, finds the matching playlist,
 * checks for existing placements, and fills FILLER slots with the content.
 *
 * @param {string} contentRecordId - Airtable record ID in content master base
 * @param {Array<{round: string, numberOfRotations: number}>} allocations - Round allocations
 * @param {object} [assetMeta] - Optional pre-fetched content metadata (skips fetch if provided)
 * @returns {Promise<{seeded: number, skipped: number, errors: string[]}>}
 */
export async function autoSeedClubContent(contentRecordId, allocations, assetMeta = null) {
  // ── DISABLED ──
  // Club content placement is now handled exclusively through the
  // "Place Club Content" button in PlaylistManager after Commercial QA approval.
  // Auto-seeding bypassed the approval gate and could overwrite commercial content.
  //
  // Flow: Club LEDPlaylistView → addLEDPlaylistSlots (creates club-type staging slots)
  //       → Commercial QA approves → Operator clicks "Place Club Content"
  //       → handleAutoPopulateClub moves approved club slots into filler positions.
  console.log(`[autoSeed] DISABLED — club content is placed via "Place Club Content" button after Commercial QA approval`);
  return { seeded: 0, skipped: 0, errors: [] };
}


// ============================================================
// AUTO-SEED: Push club TVC content into TVC Reel Slots
// Called from IngestFlow Gallery when clubs save round allocations on TVC content
// ============================================================

/**
 * Auto-seed club TVC content into TVC Reel Slots.
 *
 * For each round in the allocations, finds matching TVC Reels by venue + round,
 * then creates TVC Reel Slot records with Source = "CLUB".
 *
 * @param {string} contentRecordId - Airtable record ID in content master base
 * @param {Array<{round: string, numberOfRotations: number}>} allocations - Round allocations
 * @param {object} [assetMeta] - Optional pre-fetched content metadata
 * @returns {Promise<{seeded: number, skipped: number, errors: string[]}>}
 */
export async function autoSeedClubTVCContent(contentRecordId, allocations, assetMeta = null) {
  const result = { seeded: 0, skipped: 0, errors: [] };

  if (!allocations || allocations.length === 0) return result;
  if (!TABLES.tvcReels || !TABLES.tvcReelSlots) {
    result.errors.push("TVC Reel tables not configured");
    return result;
  }

  try {
    // 1. Get content metadata
    const content = assetMeta || await fetchContentRecord(contentRecordId);
    if (!content || !content.venue) {
      result.errors.push("Could not determine content venue");
      return result;
    }
    console.log(`[autoSeedTVC] Content: "${content.contentName}" at venue="${content.venue}"`);

    // 2. Fetch all TVC Reels and fixtures for round resolution
    const [allReels, fixturesRaw] = await Promise.all([
      getTVCReels(),
      getFixturesAsStrings().catch(() => []),
    ]);
    console.log(`[autoSeedTVC] Total TVC reels fetched: ${allReels.length}`);

    const getField = (val) => Array.isArray(val) ? val[0] : val;

    // Build fixtures map
    const fixMap = {};
    fixturesRaw.forEach((r) => {
      const f = r.fields;
      const roundName = getField(f["Round Name"]) || getField(f["Round Label"]) || "";
      const roundNum = f["Round Number"] ?? null;
      const roundRaw = getField(f["Round"]) || "";
      let round = "";
      if (roundName && !roundName.startsWith("rec")) round = roundName;
      else if (roundNum !== null && roundNum !== undefined) round = roundNum === 0 ? "OR" : "R" + roundNum;
      else if (roundRaw && !roundRaw.startsWith("rec")) round = roundRaw;
      fixMap[r.id] = { round, venue: getField(f["Venue Name"]) || getField(f["Venue"]) || "" };
    });

    // Parse reels with venue/round
    const isRecId = (v) => typeof v === "string" && /^rec[A-Za-z0-9]{10,}$/.test(v);
    const parsedReels = allReels.map((r) => {
      const f = r.fields;
      const fixtureId = getField(f["Fixture"]) || "";
      const fix = fixMap[fixtureId] || {};
      const roundRaw = getField(f["Round"]) || getField(f["Round Name"]) || "";
      const rawVenue = getField(f["Venue"]) || getField(f["Venue Name"]) || "";
      return {
        id: r.id,
        fixtureId,
        venue: (rawVenue && !isRecId(rawVenue)) ? rawVenue : fix.venue || "",
        round: normaliseRoundLabel((roundRaw && !isRecId(roundRaw)) ? roundRaw : fix.round || ""),
        reelNumber: f["Reel Number"] || f["Number"] || 0,
      };
    });

    // Filter to matching venue (fuzzy: "Marvel" matches "Marvel Stadium")
    const contentVenue = (content.venue || "").toLowerCase();
    const venueMatchesTVC = (pVenue) => {
      const pv = (pVenue || "").toLowerCase();
      return pv === contentVenue || pv.includes(contentVenue) || contentVenue.includes(pv);
    };
    const venueReels = parsedReels.filter(r => venueMatchesTVC(r.venue));
    const uniqueVenues = [...new Set(parsedReels.map(r => r.venue))];
    console.log(`[autoSeedTVC] Looking for venue "${content.venue}" — available: ${JSON.stringify(uniqueVenues)}`);
    console.log(`[autoSeedTVC] ${venueReels.length} reels matched for venue "${content.venue}"`);

    if (venueReels.length === 0) {
      console.log(`[autoSeedTVC] No existing reels for venue "${content.venue}" — will auto-create as needed`);
    }

    // 3. Fetch existing TVC Reel Slots to check for duplicates
    const existingSlots = await getAllTVCReelSlots();
    const existingByReel = {};
    existingSlots.forEach((r) => {
      const f = r.fields;
      const reelId = getField(f["Reel"]) || getField(f["TVC Reel"]) || "";
      const assetName = f["Asset Name"] || f["Name"] || "";
      if (!existingByReel[reelId]) existingByReel[reelId] = [];
      existingByReel[reelId].push({ assetName, position: f["Position"] || 0 });
    });

    // 4. Process each round allocation
    for (const alloc of allocations) {
      const targetRound = normaliseRoundLabel(alloc.round);
      const targetRotations = (alloc.numberOfRotations || alloc.rotations || 1) * 2;
      if (!targetRound) continue;

      // Find matching reels for this round
      let matchingReels = venueReels.filter(r => r.round === targetRound);

      // Auto-create ONE TVC reel if none exists for this venue+round
      if (matchingReels.length === 0) {
        const availableRounds = venueReels.map(r => r.round).filter(Boolean);
        console.warn(`[autoSeedTVC] No reel for ${content.venue} ${targetRound} — available: ${JSON.stringify(availableRounds)}`);

        const matchingFixture = Object.entries(fixMap).find(([, fx]) => {
          const fxRound = normaliseRoundLabel(fx.round);
          return venueMatchesTVC(fx.venue) && fxRound === targetRound;
        });

        if (!matchingFixture) {
          console.warn(`[autoSeedTVC] No fixture for ${content.venue} ${targetRound} — cannot auto-create reel`);
          result.skipped += targetRotations;
          result.errors.push(`No fixture exists for ${content.venue} ${targetRound} — reel cannot be created`);
          continue;
        }

        const [fixtureId] = matchingFixture;
        console.log(`[autoSeedTVC] Auto-creating TVC reel for ${content.venue} ${targetRound} (fixture ${fixtureId})`);

        try {
          const created = await createRecord(TABLES.tvcReels, {
            "Fixture": [fixtureId],
            "Status": "Draft",
          });
          console.log(`[autoSeedTVC] ✅ Created reel ${created.id}`);
          const newReel = {
            id: created.id,
            fixtureId,
            venue: content.venue,
            round: targetRound,
            reelNumber: 1,
          };
          matchingReels = [newReel];
          venueReels.push(newReel);
        } catch (err) {
          console.error(`[autoSeedTVC] Failed to auto-create reel:`, err.message);
          result.errors.push(`Failed to create TVC reel for ${content.venue} ${targetRound}: ${err.message}`);
          result.skipped += targetRotations;
          continue;
        }
      }

      // Seed into each matching reel (one slot per reel per rotation)
      for (const reel of matchingReels) {
        const existingInReel = (existingByReel[reel.id] || [])
          .filter(s => s.assetName === content.contentName);

        if (existingInReel.length >= targetRotations) {
          console.log(`[autoSeedTVC] ${targetRound} reel ${reel.reelNumber}: already has ${existingInReel.length}/${targetRotations} — skipping`);
          continue;
        }

        const toCreate = targetRotations - existingInReel.length;
        // Find next available position in this reel
        const maxPos = (existingByReel[reel.id] || []).reduce((max, s) => Math.max(max, s.position || 0), 0);

        const newSlots = [];
        for (let i = 0; i < toCreate; i++) {
          newSlots.push({
            "Reel": [reel.id],
            "Position": maxPos + i + 1,
            "Client Name": content.client,
            "Asset Name": content.contentName,
            "Content ID": content.contentId,
            "Duration": String(Math.round(parseFloat(content.duration) || 30)),
            "Source": "CLUB",
            "Notes": `Auto-seeded from club gallery`,
          });
        }

        if (newSlots.length > 0) {
          console.log(`[autoSeedTVC] ${targetRound} reel ${reel.reelNumber}: creating ${newSlots.length} slot(s)`);
          await createRecords(TABLES.tvcReelSlots, newSlots);
          result.seeded += newSlots.length;
        }
      }
    }

    console.log(`[autoSeedTVC] Done: ${result.seeded} seeded, ${result.skipped} skipped, ${result.errors.length} errors`);
    return result;
  } catch (err) {
    console.error("[autoSeedTVC] Fatal error:", err);
    result.errors.push(err.message || "Unknown error");
    return result;
  }
}

// ============================================================
// CLUB PLAYLIST MANAGEMENT — Direct allocation (no auto-seed)
// ============================================================

/**
 * Fetch TVC-tagged content for a club from content master base.
 * Matches by "Uploaded By" containing club name, and filename/destination containing "TVC".
 */
export async function getClubTVCContent(clubName) {
  if (!clubName || !CONTENT_ASSETS_TABLE) return [];
  const formula = `AND(
    OR(FIND("${clubName}", {Uploaded By}), FIND("${clubName}", {File/Folder Name})),
    OR(FIND("TVC", UPPER({Asset Destination})), FIND("TVC", UPPER({File/Folder Name})), FIND("BS-VIDEO", UPPER({File/Folder Name})))
  )`.replace(/\n\s*/g, '');
  
  const records = [];
  let offset = null;
  do {
    let url = `/${CONTENT_ASSETS_TABLE}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    url += `&filterByFormula=${encodeURIComponent(formula)}`;
    if (offset) url += `&offset=${offset}`;
    const data = await contentBaseFetch(url);
    if (data?.records) records.push(...data.records);
    offset = data?.offset || null;
  } while (offset);
  return records;
}

/**
 * Fetch LED-tagged content for a club from content master base.
 * Matches by "Uploaded By" containing club name, and destination containing LED/tiled/360.
 */
export async function getClubLEDContent(clubName) {
  if (!clubName || !CONTENT_ASSETS_TABLE) return [];
  const formula = `AND(
    OR(FIND("${clubName}", {Uploaded By}), FIND("${clubName}", {File/Folder Name})),
    OR(FIND("led", LOWER({Asset Destination})), FIND("tiled", LOWER({Asset Destination})), FIND("360", LOWER({Asset Destination})))
  )`.replace(/\n\s*/g, '');
  
  const records = [];
  let offset = null;
  do {
    let url = `/${CONTENT_ASSETS_TABLE}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    url += `&filterByFormula=${encodeURIComponent(formula)}`;
    if (offset) url += `&offset=${offset}`;
    const data = await contentBaseFetch(url);
    if (data?.records) records.push(...data.records);
    offset = data?.offset || null;
  } while (offset);
  return records;
}

/**
 * Fetch fixtures from commercial base filtered by club home team name.
 * Returns with cellFormat=string so linked records resolve to display text.
 */
export async function getClubFixturesForTeam(clubName) {
  if (!clubName || !TABLES.fixtures) return [];
  // FIND at position 1 = team name starts the string (prevents "Melbourne" matching "North Melbourne")
  // Venue filter restricts to MCG & Marvel — the two VenueFlow-managed venues.
  const formula = `AND(FIND("${clubName}", {Home Team}) = 1, OR(FIND("MCG", {Venue}), FIND("Marvel", {Venue})))`;
  const records = [];
  let offset = null;
  do {
    let url = `/${TABLES.fixtures}?cellFormat=string&timeZone=Australia/Melbourne&userLocale=en-au&pageSize=100`;
    url += `&filterByFormula=${encodeURIComponent(formula)}`;
    url += `&sort[0][field]=Date&sort[0][direction]=asc`;
    if (offset) url += `&offset=${offset}`;
    const data = await airtableFetch(url);
    if (data?.records) records.push(...data.records);
    offset = data?.offset || null;
  } while (offset);
  console.log(`[getClubFixturesForTeam] "${clubName}" → ${records.length} fixtures (MCG + Marvel only)`);
  return records;
}

/**
 * Ensure a TVC Reel exists for a fixture. Returns reel record ID.
 * If no reel exists, creates one with Status=Draft.
 */
export async function ensureTVCReel(fixtureId) {
  if (!fixtureId || !TABLES.tvcReels) return null;

  // Check for existing reel linked to this fixture
  const formula = `FIND("${fixtureId}", ARRAYJOIN({Fixture}))`;
  let url = `/${TABLES.tvcReels}?filterByFormula=${encodeURIComponent(formula)}&maxRecords=1`;
  const data = await airtableFetch(url);
  if (data?.records?.length > 0) {
    return data.records[0].id;
  }

  // Create new reel
  const created = await createRecord(TABLES.tvcReels, {
    "Fixture": [fixtureId],
    "Status": "Draft",
  });
  console.log(`[ensureTVCReel] Created reel ${created.id} for fixture ${fixtureId}`);
  return created.id;
}

/**
 * Create TVC Reel Slot records.
 * @param {string} reelId — TVC Reel record ID
 * @param {object} content — { name, contentId, client, duration }
 * @param {number} plays — number of slots to create
 * @param {Array} existingSlots — already in this reel, to calculate next position
 */
export async function addTVCReelSlots(reelId, content, plays, existingSlots = []) {
  if (!reelId || !TABLES.tvcReelSlots || plays <= 0) return [];
  const maxPos = existingSlots.reduce((max, s) => Math.max(max, s.position || 0), 0);
  
  const newSlots = [];
  for (let i = 0; i < plays; i++) {
    newSlots.push({
      "Reel": [reelId],
      "Position": maxPos + i + 1,
      "Client Name": content.client || "",
      "Asset Name": content.name || "",
      "Content ID": content.contentId || "",
      "Duration": String(Math.round(parseFloat(content.duration) || 30)),
      "Source": "CLUB",
      "Notes": "Allocated via club playlist view",
    });
  }

  const results = [];
  for (let i = 0; i < newSlots.length; i += 10) {
    const batch = newSlots.slice(i, i + 10);
    const data = await airtableFetch(`/${TABLES.tvcReelSlots}`, {
      method: "POST",
      body: JSON.stringify({ records: batch.map(fields => ({ fields })) }),
    });
    if (data?.records) results.push(...data.records);
  }
  return results;
}

/**
 * Ensure an LED Playlist exists for a fixture. Returns playlist record ID.
 * If no playlist exists, creates one with Status=Generated.
 */
export async function ensureLEDPlaylist(fixtureId) {
  // ── DISABLED ──
  // Club LED content stays in the club LED playlist — it does NOT create or touch
  // commercial playlists. Even after CommercialQA approval, club content remains
  // in the club LED playlist and is placed into the commercial playlist manually
  // by the operator via "Place Club Content" in CommercialFlow.
  console.log(`[ensureLEDPlaylist] DISABLED — club content does not create commercial playlists`);
  return null;
}

/**
 * Create LED Playlist Slot records for club content.
 * Distributes plays across Q1–Q4 breaks.
 * @param {string} playlistId — LED Playlist record ID
 * @param {object} content — { name, contentId, client, duration, filePath, tier, category, colour }
 * @param {number} plays — total plays across the game (distributed across 4 breaks)
 * @param {Array} existingSlots — already in this playlist (for position calculation)
 */
export async function addLEDPlaylistSlots(playlistId, content, plays, existingSlots = [], isNewPlaylist = false) {
  // ── DISABLED ──
  // Club LED content does NOT write to the commercial playlist slots table.
  // Club content is managed entirely within the club LED playlist.
  // CommercialQA approval locks it in the club playlist — it does not push to commercial.
  console.log(`[addLEDPlaylistSlots] DISABLED — club content does not write to commercial playlist slots`);
  return [];
}

/**
 * Delete LED Playlist Slot records by IDs.
 */
export async function deleteLEDPlaylistSlots(slotIds) {
  if (!TABLES.playlistSlots || !slotIds || slotIds.length === 0) return;
  for (let i = 0; i < slotIds.length; i += 10) {
    const batch = slotIds.slice(i, i + 10);
    const params = batch.map(id => `records[]=${id}`).join("&");
    await airtableFetch(`/${TABLES.playlistSlots}?${params}`, { method: "DELETE" });
  }
}

// ============================================================
// EXPORT ALL
// ============================================================

/**
 * ─── TVC Reel CRUD (for CommercialFlow operator views) ───
 *
 * These functions support the CommercialFlow TVC Playlist operator view,
 * providing full CRUD for TVC Reels and Slots alongside the club-facing
 * ensureTVCReel/addTVCReelSlots functions above.
 */

/** Create a new TVC Reel record */
export async function createTVCReel(data) {
  if (!TABLES.tvcReels) throw new Error("TVC Reels table not configured");
  return createRecord(TABLES.tvcReels, data);
}

/** Get a single TVC Reel by record ID */
export async function getTVCReel(recordId) {
  if (!TABLES.tvcReels) return null;
  return fetchOne(TABLES.tvcReels, recordId);
}

/** Update a TVC Reel */
export async function updateTVCReel(recordId, data) {
  if (!TABLES.tvcReels) return null;
  return updateRecord(TABLES.tvcReels, recordId, data);
}

/** Delete a TVC Reel */
export async function deleteTVCReel(recordId) {
  if (!TABLES.tvcReels || !recordId) return;
  return deleteRecord(TABLES.tvcReels, recordId);
}

/** Get TVC Reels for a specific fixture */
export async function getTVCReelsByFixture(fixtureId) {
  if (!TABLES.tvcReels || !fixtureId) return [];
  const formula = `FIND("${fixtureId}", ARRAYJOIN({Fixture}))`;
  const url = `/${TABLES.tvcReels}?filterByFormula=${encodeURIComponent(formula)}`;
  const data = await airtableFetch(url);
  return data?.records || [];
}

/** Create a single TVC Reel Slot */
export async function createTVCReelSlot(data) {
  if (!TABLES.tvcReelSlots) throw new Error("TVC Reel Slots table not configured");
  return createRecord(TABLES.tvcReelSlots, data);
}

/** Create multiple TVC Reel Slots */
export async function createTVCReelSlots(records) {
  if (!TABLES.tvcReelSlots || !records.length) return [];
  return createRecords(TABLES.tvcReelSlots, records);
}

/** Update a TVC Reel Slot */
export async function updateTVCReelSlot(recordId, data) {
  if (!TABLES.tvcReelSlots || !recordId) return null;
  return updateRecord(TABLES.tvcReelSlots, recordId, data);
}

/** Get TVC Reel Slots for a specific reel */
export async function getTVCReelSlotsByReel(reelId) {
  if (!TABLES.tvcReelSlots || !reelId) return [];
  const formula = `FIND("${reelId}", ARRAYJOIN({Reel}))`;
  const url = `/${TABLES.tvcReelSlots}?filterByFormula=${encodeURIComponent(formula)}`;
  const data = await airtableFetch(url);
  return (data?.records || []).sort((a, b) => (a.fields?.Position || 0) - (b.fields?.Position || 0));
}
// ============================================================
// COMMERCIAL ASSETS — ARCHIVE SYNC
// ============================================================
// These functions ONLY sync the Archived flag from the club base
// to the commercial base. They do NOT copy content, playlists,
// or any other data. Used by archiveAsset/restoreAsset in airtable.js
// to keep archive state in sync across the two bases.
// ============================================================

/**
 * Find an asset in the commercial base by its Lumen8 ID.
 * Returns the matching record or null if not found.
 * @param {string} lumen8Id - The Lumen8 ID to search for
 * @returns {Promise<Object|null>}
 */
export async function findCommercialAssetByLumen8Id(lumen8Id) {
  if (!lumen8Id) return null;
  try {
    // Content Library lives in the CONTENT base (appKEnvtQmLtNO17d), not the
    // commercial deals base. Use contentBaseFetch + CONTENT_ASSETS_TABLE so we
    // hit the same base/table the upload-mirror (createContentRecord) writes to.
    const safe = String(lumen8Id).replace(/"/g, '\\"');
    const formula = `OR({Lumen8 ID}="${safe}",{File/Folder Name}="${safe}")`;
    const params = new URLSearchParams({ filterByFormula: formula, maxRecords: "1", pageSize: "1" });
    const result = await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}?${params.toString()}`);
    return result?.records?.[0] || null;
  } catch (err) {
    console.warn(`[commercialAirtable] findCommercialAssetByLumen8Id failed for ${lumen8Id}:`, err.message);
    return null;
  }
}

/**
 * Set the Archived flag on a commercial asset record.
 * Does NOT touch any other fields. Returns true on success, false on failure.
 * @param {string} recordId - The Airtable record ID in the commercial base
 * @param {boolean} archived - true to archive, false to restore
 * @returns {Promise<boolean>}
 */
export async function setCommercialAssetArchived(recordId, archived) {
  if (!recordId) return false;
  try {
    // Write to the CONTENT base (appKEnvtQmLtNO17d), same base/table the
    // upload mirror creates rows in. Field name is the literal checkbox
    // "Archived" on CONTENT_ASSETS_TABLE.
    await contentBaseFetch(`/${CONTENT_ASSETS_TABLE}`, {
      method: "PATCH",
      body: JSON.stringify({
        records: [{ id: recordId, fields: { Archived: !!archived } }],
      }),
    });
    return true;
  } catch (err) {
    console.warn(`[commercialAirtable] setCommercialAssetArchived failed for ${recordId}:`, err.message);
    return false;
  }
}

/**
 * Convenience: sync archive state by Lumen8 ID. Looks up the commercial
 * record and updates its Archived flag in one call.
 * @param {string} lumen8Id
 * @param {boolean} archived
 * @returns {Promise<{found: boolean, updated: boolean}>}
 */
export async function syncCommercialAssetArchiveByLumen8Id(lumen8Id, archived) {
  const record = await findCommercialAssetByLumen8Id(lumen8Id);
  if (!record) return { found: false, updated: false };
  const updated = await setCommercialAssetArchived(record.id, archived);
  return { found: true, updated };
}

const commercialAirtable = {
  // Inventory
  getInventory,
  getInventoryByCompetition,
  getInventoryBySource,
  createInventory,
  updateInventory,
  deleteInventory,
  getAvailableInventory,
  recalculateInventoryAvailable,
  
  // Deals
  getDeals,
  getDealsByStatus,
  getDealsByBM,
  getDealsByClient,
  getDeal,
  createDeal,
  updateDeal,
  deleteDeal,
  
  // Booking Lines
  getBookingLines,
  getBookingLinesByDeal,
  getBookingLine,
  createBookingLine,
  linkBookingLineToDeal,
  updateBookingLine,
  deleteBookingLine,
  getSoldMinutes,
  checkInventoryAvailability,
  
  // Entitlements
  getEntitlements,
  getEntitlementsByBookingLine,
  getEntitlementsByClient,
  getEntitlementsByFixture,
  getEntitlement,
  createEntitlements,
  updateEntitlement,
  deleteEntitlement,
  assignContent,
  
  // Playlists
  getPlaylists,
  getPlaylistByFixture,
  getPlaylistByFixtureScoped,
  getPlaylistsByStatus,
  getPlaylist,
  getPlaylistSlots,
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  approvePlaylist,
  lockPlaylist,
  markPlaylistSynced,
  
  // Reference tables
  getFixtures,
  getFixturesAsStrings,
  getFixturesByVenue,
  getFixturesByRounds,
  getRoundsByCompetition,
  getVenueFixtureRounds,
  getFixture,
  getClubHomeRounds,
  getPartners,
  getPartnersByType,
  getVenues,
  getVenuesByCompetition,
  getAssets,
  getAssetsByClient,
  getApprovedAssets,
  
  // TVC Reels & Slots
  getTVCReels,
  getTVCReel,
  getTVCReelsByFixture,
  createTVCReel,
  updateTVCReel,
  deleteTVCReel,
  getAllTVCReelSlots,
  getTVCReelSlotsByReel,
  createTVCReelSlot,
  createTVCReelSlots,
  updateTVCReelSlot,
  deleteTVCReelSlot,
  getFixtureRound,
  updateTVCReelSlotStatus,

  // Playlist Slots (LED commercial)
  getAllPlaylistSlots,
  getPlaylistSlotsByPlaylist,
  createPlaylistSlots,
  createPlaylistSlotsParallel,
  clearPlaylistSlots,
  deletePlaylistSlots,
  deletePlaylistSlot,
  updatePlaylistSlot,
  deletePlaylistSlotsByPlaylist,
  batchUpdatePlaylistSlots,
  updatePlaylistSlotStatus,

  // Auto-seed (club → commercial playlist)
  fetchContentRecord,
  autoSeedClubContent,
  autoSeedClubTVCContent,
  autoFillAllClubContent,

  // Content Master Base
  removeRoundFromContent,
  fetchLumen8ToAssetIdMap,
  fetchRoundAllocationsMap,
  lookupContentRecordByLumenId,
  createContentRecord,
  updateContentRoundAllocations,
  fetchContentPreviews,
  fetchGroupedAssetLinks,

  // Club playlist management (direct allocation)
  getClubTVCContent,
  getClubLEDContent,
  getClubFixturesForTeam,
  ensureTVCReel,
  addTVCReelSlots,
  ensureLEDPlaylist,
  addLEDPlaylistSlots,
  deleteLEDPlaylistSlots,
  // Archive sync (Lumen8 ID match — does not copy content)
  findCommercialAssetByLumen8Id,
  setCommercialAssetArchived,
  syncCommercialAssetArchiveByLumen8Id,
};

export default commercialAirtable;
